import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, from } from 'rxjs';
import { BehaviourSubjectService } from '../services/behaviour-subject.service';
import { CookieService } from '../services/cookie.service';
import { RootService } from '../services/root.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { switchMap, take, filter, finalize, mergeMap } from 'rxjs/operators';
import * as CryptoJS from 'crypto-js';
import { formatDate, isPlatformBrowser } from '@angular/common';
import { secureStorage } from '../services/secure-storage.service';
import { TokenService } from '../services/token.service';
import { yohainous } from '../../core-node';
import { applicationConfiguration, AppConfig } from './app.config';
import { ApiService } from '../services/api.service';
import { environment } from '../../../environment/environment';
import { NotificationService } from '../services/notification.service';

export const REQUEST_NONCE = new HttpContextToken<string>(() => '');

@Injectable()
export class MyHttpInterceptor implements HttpInterceptor {
  isLoggedIn: boolean = false;
  private refreshTokenInProgress = false;
  private refreshTokenSubject: Subject<any> = new BehaviorSubject<any>(null);
  private generateTokenInProgress = false;
  private generateTokenSubject: Subject<any> = new BehaviorSubject<any>(null);

  // Only these API endpoints will use MITM nonce + response signature verification.
  private readonly mitmProtectedPathParts: string[] = [
    'account/update_default_address/',
    'account/user_profile/',
    'account/login_verify_otp/',
    'account/user_registration/',
    // 'cart/wishlist/',
    'account/update_send_otp/',
    'account/update_user_verify_otp/',
    'account/user_address_list/',
    'order/create_order/',
    'order/cancellation_request/', // covers order/cancellation_request/<order...>
    // 'order/invoice/',
    'cart/buyer_best_offer/',
  ];

  constructor(
    private behaviorService: BehaviourSubjectService,
    public router: Router,
    public rootService: RootService,
    private ssrcookieService: CookieService,
    private localStorage: secureStorage,
    private tokenService: TokenService,
    private apiService: ApiService,
    private notificationService: NotificationService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(applicationConfiguration) private appConfig: AppConfig,
  ) {}

  /** Runs token injection, optional MITM nonce headers, and response signature verification. */
  private handleRequest(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const injected = this.injectToken(request);
    const { req, mitmActive } = this.attachMitmHeaders(injected);
    // Fast path: for non-MITM endpoints, avoid async promise bridge in the
    // interceptor chain to reduce teardown sensitivity on component destroy.
    if (!mitmActive) {
      return next.handle(req);
    }
    return next
      .handle(req)
      .pipe(
        mergeMap((event) =>
          from(this.verifyMitmResponse(event, req, mitmActive)),
        ),
      );
  }

  private isEolBackendRequest(url: string): boolean {
    const base = this.appConfig.apiUrl.replace(/\/+$/, '');
    return url.startsWith(base);
  }

  private isMitmProtectedEndpoint(url: string): boolean {
    // URL can include base + 'api/eol/v1/pv/' + <path>; we match by known path parts.
    return this.mitmProtectedPathParts.some((p) => url.includes(p));
  }

  private attachMitmHeaders(request: HttpRequest<any>): {
    req: HttpRequest<any>;
    mitmActive: boolean;
  } {
    if (!isPlatformBrowser(this.platformId)) {
      return { req: request, mitmActive: false };
    }
    if (!this.isEolBackendRequest(request.url)) {
      return { req: request, mitmActive: false };
    }
    if (request.url.includes('get_public_key')) {
      return { req: request, mitmActive: false };
    }
    if (!this.isMitmProtectedEndpoint(request.url)) {
      return { req: request, mitmActive: false };
    }
    const nonce = crypto.randomUUID();
    const req = request.clone({
      setHeaders: { 'X-Request-Nonce': nonce },
      context: request.context.set(REQUEST_NONCE, nonce),
    });
    return { req, mitmActive: true };
  }

  private async verifyMitmResponse(
    event: HttpEvent<any>,
    request: HttpRequest<any>,
    mitmActive: boolean,
  ): Promise<HttpEvent<any>> {
    if (!(event instanceof HttpResponse) || !mitmActive) {
      return event;
    }
    const signature = event.headers.get('X-Response-Signature');
    if (!signature) {
      return event;
    }
    const requestNonce = request.context.get(REQUEST_NONCE);
    const responseNonce = event.headers.get('X-Response-Nonce');
    if (!responseNonce || responseNonce !== requestNonce) {
      throw new HttpErrorResponse({
        status: 498,
        statusText: 'Invalid Nonce / Possible Replay Attack',
        url: event.url || undefined,
      });
    }
    const responseText = JSON.stringify(event.body);
    const serverPublicKey = await this.apiService.getMitmServerPublicKey();
    const isValid = await this.verifyMitmSignature(
      requestNonce,
      signature,
      serverPublicKey,
      responseText,
    );
    if (!isValid) {
      // Fail-open in non-production to avoid breaking while backend signature
      // canonicalization/format is being aligned. Fail-closed in production.
      if (environment.production) {
        throw new HttpErrorResponse({
          status: 498,
          statusText: 'Invalid Signature / Response Tampered',
          url: event.url || undefined,
        });
      }
      // this.notificationService.error(
      //   'Security verification failed for this response. Please try again. If the issue persists, refresh the page.',
      // );
      // eslint-disable-next-line no-console
      console.warn('[MITM] Invalid response signature (non-prod, allowing)', {
        url: event.url,
      });
    }
    return event;
  }

  private mitmBase64Decode(base64String: string): ArrayBuffer {
    const normalized = base64String
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(base64String.length / 4) * 4, '=');
    const binaryString = atob(normalized);
    const MAX_BINARY_STRING_LENGTH = 200000;
    if (binaryString.length > MAX_BINARY_STRING_LENGTH) {
      return new ArrayBuffer(0);
    }
    const safeLen = binaryString.length;
    const bytes = new Uint8Array(safeLen);
    for (let i = 0; i < safeLen; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private async mitmToEcdsaP256PublicKey(
    publicKeyOrPem: unknown,
  ): Promise<CryptoKey | null> {
    try {
      if (
        publicKeyOrPem &&
        typeof publicKeyOrPem === 'object' &&
        (publicKeyOrPem as CryptoKey).type
      ) {
        return publicKeyOrPem as CryptoKey;
      }
      const pem = String(publicKeyOrPem || '');
      if (!pem.includes('BEGIN PUBLIC KEY')) {
        return null;
      }
      const b64 = pem
        .replace(/-----BEGIN PUBLIC KEY-----/g, '')
        .replace(/-----END PUBLIC KEY-----/g, '')
        .replace(/\s+/g, '');
      const binaryDer = atob(b64);
      const bytes = new Uint8Array(binaryDer.length);
      for (let i = 0; i < binaryDer.length; i++) {
        bytes[i] = binaryDer.charCodeAt(i);
      }
      return await crypto.subtle.importKey(
        'spki',
        bytes.buffer,
        { name: 'ECDSA', namedCurve: 'P-256' },
        true,
        ['verify'],
      );
    } catch {
      return null;
    }
  }

  private async verifyMitmSignature(
    requestNonce: string | null,
    responseSignature: string | null,
    publicKeyPem: string | null,
    responseJson: string | null,
  ): Promise<boolean> {
    try {
      if (
        !requestNonce ||
        !responseSignature ||
        !publicKeyPem ||
        responseJson == null
      ) {
        return false;
      }
      const signaturePayload = responseJson + '|' + requestNonce;
      const signatureBytes = this.mitmBase64Decode(String(responseSignature));
      const payloadBytes = new TextEncoder().encode(signaturePayload);
      const cryptoKey = await this.mitmToEcdsaP256PublicKey(publicKeyPem);
      if (!cryptoKey) {
        return false;
      }
      const isValid = await crypto.subtle.verify(
        { name: 'ECDSA', hash: 'SHA-256' },
        cryptoKey,
        signatureBytes,
        payloadBytes,
      );
      return !!isValid;
    } catch {
      return false;
    }
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Skip interceptor for token generation and refresh endpoints
    if (
      request.url.indexOf('generate_token') !== -1 ||
      request.url.indexOf('refresh_token') !== -1
    ) {
      return this.handleRequest(request, next);
    }

    const accessToken = this.tokenService.getAccessToken();
    const refreshToken = this.tokenService.getRefreshToken();
    const userCode = this.tokenService.getUserCode();

    // Check if localStorage was cleared (no user code but tokens exist in localStorage)
    // Only regenerate if token is invalid or expiring
    if ((accessToken || refreshToken) && !userCode) {
      // If we have a valid token, proceed normally instead of regenerating
      if (accessToken) {
        const tokenParts = accessToken.split('.');
        if (
          tokenParts.length === 3 &&
          tokenParts[1] &&
          tokenParts[1].length > 0
        ) {
          // Token has valid structure, check if it's expiring
          if (!this.tokenService.isTokenExpiringSoon(30)) {
            // Token is valid, proceed normally
            return this.handleRequest(request, next);
          }
        }
      }
      // Token is invalid or expiring, generate new one
      return this.handleTokenGeneration(request, next);
    }

    // Case 1: User is authenticated (has both token and user code)
    if (accessToken && userCode) {
      // Only check expiration if token has valid JWT structure
      const tokenParts = accessToken.split('.');
      if (
        tokenParts.length === 3 &&
        tokenParts[1] &&
        tokenParts[1].length > 0
      ) {
        // Check if token is expiring soon (within 30 seconds)
        if (this.tokenService.isTokenExpiringSoon(30)) {
          return this.handleTokenRefresh(request, next);
        }
      }

      // Token is valid, proceed normally
      return this.handleRequest(request, next);
    }

    // Case 2: User has access token but no user code (free search scenario)
    if (accessToken && !userCode) {
      // Only check expiration if token has valid JWT structure
      const tokenParts = accessToken.split('.');
      if (
        tokenParts.length === 3 &&
        tokenParts[1] &&
        tokenParts[1].length > 0
      ) {
        // Check if token is expiring soon (within 30 seconds)
        if (this.tokenService.isTokenExpiringSoon(30)) {
          return this.handleTokenGeneration(request, next);
        }
      }

      // Token is valid, proceed normally
      return this.handleRequest(request, next);
    }

    // Case 3: User has refresh token and user code but no access token
    if (refreshToken && userCode && !accessToken) {
      return this.handleTokenRefresh(request, next);
    }

    // Case 4: No authentication at all - generate token for free search
    if (!accessToken && !refreshToken && !userCode) {
      return this.handleTokenGeneration(request, next);
    }

    // Default case: inject whatever tokens we have
    return this.handleRequest(request, next);
  }

  private handleTokenGeneration(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // If token generation is already in progress, wait for it
    if (this.generateTokenInProgress) {
      return this.generateTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap(() => {
          return this.handleRequest(request, next);
        }),
      );
    }

    // Check if localStorage was cleared (no user code but tokens exist)
    const userCode = this.tokenService.getUserCode();
    const accessToken = this.tokenService.getAccessToken();
    const refreshToken = this.tokenService.getRefreshToken();

    // Double-check: if we have a valid token that's not expiring, don't generate
    if (accessToken) {
      const tokenParts = accessToken.split('.');
      if (
        tokenParts.length === 3 &&
        tokenParts[1] &&
        tokenParts[1].length > 0
      ) {
        if (
          !this.tokenService.isTokenExpired() &&
          !this.tokenService.isTokenExpiringSoon(30)
        ) {
          // Token is valid, proceed with request
          return this.handleRequest(request, next);
        }
      }
    }

    this.generateTokenInProgress = true;
    this.generateTokenSubject.next(null);

    // If localStorage is cleared but tokens exist, always generate free search token
    if ((accessToken || refreshToken) && !userCode) {
      const tokenPromise = this.rootService.generateTokenForFreeSearch();

      return from(tokenPromise).pipe(
        switchMap(() => {
          this.generateTokenInProgress = false;
          this.generateTokenSubject.next('completed');
          return this.handleRequest(request, next);
        }),
        finalize(() => {
          this.generateTokenInProgress = false;
        }),
      );
    }

    // Use appropriate method based on whether user is logged in
    const tokenPromise = userCode
      ? this.rootService.getToken()
      : this.rootService.generateTokenForFreeSearch();

    return from(tokenPromise).pipe(
      switchMap(() => {
        this.generateTokenInProgress = false;
        this.generateTokenSubject.next('completed');
        return this.handleRequest(request, next);
      }),
      finalize(() => {
        this.generateTokenInProgress = false;
      }),
    );
  }

  private handleTokenRefresh(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // If refresh is already in progress, wait for it
    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap(() => {
          return this.handleRequest(request, next);
        }),
      );
    }

    // Double-check: if we have a valid token that's not expiring, don't refresh
    const accessToken = this.tokenService.getAccessToken();
    if (accessToken) {
      const tokenParts = accessToken.split('.');
      if (
        tokenParts.length === 3 &&
        tokenParts[1] &&
        tokenParts[1].length > 0
      ) {
        if (
          !this.tokenService.isTokenExpired() &&
          !this.tokenService.isTokenExpiringSoon(30)
        ) {
          // Token is valid, proceed with request
          return this.handleRequest(request, next);
        }
      }
    }

    this.refreshTokenInProgress = true;
    this.refreshTokenSubject.next(null);

    if (isPlatformBrowser(this.platformId)) {
      if (this.localStorage.getItem('eolrefreshTokenInProgress') !== 'true') {
        this.localStorage.setItem('eolrefreshTokenInProgress', 'true');

        return this.rootService.refreshToken2().pipe(
          switchMap((authResponse) => {
            if (authResponse) {
              this.refreshTokenInProgress = false;
              this.localStorage.setItem('eolrefreshTokenInProgress', 'false');
            }
            this.refreshTokenSubject.next(authResponse?.data?.refresh_token);
            return this.handleRequest(request, next);
          }),
          finalize(() => {
            this.refreshTokenInProgress = false;
          }),
        );
      }
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap(() => {
        return this.handleRequest(request, next);
      }),
    );
  }

  getKey(): string {
    const activationDate = new Date();
    let newDate = formatDate(
      activationDate,
      'yyyy-MM-dd' + 'T' + 'HH:mm:ss',
      'en-GB',
    );
    newDate = newDate + 'Z';
    const vectorAxis = yohainous();
    var quan = vectorAxis.antManWaspK;
    var hype = vectorAxis.ghomoraThaS;
    var bytes = CryptoJS.PBKDF2(quan, hype, { keySize: 48, iterations: 128 });
    var iv = CryptoJS.enc.Hex.parse(bytes.toString().slice(0, 32));
    var key = CryptoJS.enc.Hex.parse(bytes.toString().slice(32, 96));
    var encrypteDate = CryptoJS.AES.encrypt(newDate, key, {
      iv: iv,
    }).toString();
    return encrypteDate;
  }

  injectToken(request: HttpRequest<any>) {
    const token = this.tokenService.getAccessToken();
    const userCode = this.tokenService.getUserCode();
    const encryptedDate = this.getKey();

    // For token generation requests, use encrypted date as user code
    if (request.url.indexOf('generate_token') !== -1) {
      const modifiedRequest = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token || ''}`,
          'user-code': encryptedDate,
        },
      });
      return modifiedRequest;
    }

    // For authenticated requests with both token and user code
    if (token && userCode) {
      const modifiedRequest = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
          'user-code': userCode,
        },
      });
      return modifiedRequest;
    }

    // For requests with only token (free search scenarios)
    if (token) {
      const modifiedRequest = request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
      return modifiedRequest;
    }

    // For requests without any authentication
    // Don't set Content-Type for FormData requests (let browser set it automatically)
    if (request.body instanceof FormData) {
      return request;
    }

    const modifiedRequest = request.clone({
      setHeaders: {
        'Content-Type': 'application/json',
      },
    });
    return modifiedRequest;
  }
}
